import React, { useMemo, useState } from 'react';
import { DocumentationFile } from '../../../../graphql/graphql';
import Text from '../../../BaseModule/Components/Text';
import FormattedMomentDate, {
	DateFormat,
} from '../FormattedMomentDate/FormattedMomentDate';
import useDocumentationFilePages from '../../../Documentation/Hooks/useDocumentationFilePages';
import useTranslation from '../../../../Hooks/useTranslation';
import styles from './MultiMediaViewer.module.scss';
import MultiMediaViewerItem from './MultiMediaViewerItem';
import apiClient from '../../../../Services/ApiClient';
import useApiClient from '../../../../Hooks/useApiClient';

interface Props {
	file: DocumentationFile;
}

const MultiMediaViewer: React.FC<Props> = ({ file }) => {
	const { t } = useTranslation();
	const { pages } = useDocumentationFilePages(file);
	const [isSelectionMode, setIsSelectionMode] = useState(false);
	const [selectedFiles, setSelectedFiles] = useState<DocumentationFile[]>([]);
	const label: string = useMemo(() => {
		return `${file.displayName} ${pages} - ${t('base.shared.inserted')} ${
			file.createdBy?.profile?.fullName
		}`;
	}, [file]);
	const { displaySuccess, displayError } = useApiClient();

	const handleSelectItem = (item: DocumentationFile, isSelected: boolean) => {
		setSelectedFiles(prev => {
			if (isSelected) {
				return [...prev, item];
			}
			return prev.filter(({ id }) => id !== item.id);
		});
	};

	const handleDownloadFiles = async () => {
		try {
			const response = await apiClient.post(
				`/document/download/create-multiple`,
				{
					ids: selectedFiles.map(item => item.id),
				},
			);
			displaySuccess(t('component.multiMediaViewer.downloadRequested'));
			setIsSelectionMode(false);
		} catch (e: any) {
			displayError(e);
		}
	};

	return (
		<div>
			<FormattedMomentDate
				date={file.createdAt}
				format={DateFormat.DATE_TIME}
			/>
			<Text tx={label} />
			<div className={styles.zoom}>
				<img src='/dist/icons/zoom.svg' alt='zoom icon' />
				<Text tx='documentation.clickToFileToShowZoomablePreview' />
			</div>
			<div className={styles.wrapper}>
				{file.groupFiles?.map((item, index) => (
					<MultiMediaViewerItem
						file={item as DocumentationFile}
						key={index}
						isSelected={selectedFiles.some(
							selectedFile => selectedFile.id === item?.id,
						)}
						isSelectionMode={isSelectionMode}
						onSelect={handleSelectItem}
					/>
				))}
			</div>
			<div className={styles.buttonWrapper}>
				{isSelectionMode ? (
					<>
						<div
							className='btn btn-light px-5'
							onClick={() => setIsSelectionMode(false)}
						>
							{t('base.shared.cancel')}
						</div>
						<div className='btn btn-primary px-5' onClick={handleDownloadFiles}>
							{t('component.multiMediaViewer.downloadDocumentsInPDF')}
						</div>
					</>
				) : (
					<div
						className='btn btn-primary px-5'
						onClick={() => setIsSelectionMode(true)}
					>
						{t('base.selectDownload')}
					</div>
				)}
			</div>
		</div>
	);
};

export default MultiMediaViewer;
